import { useState, useEffect, useCallback, type RefObject } from "react";
import type { RankingState } from "../hooks";
import { useAppMode, useRankingContext } from "../hooks";
import type { RankingInput, GenericRankingInput, RankingCreateResponse } from "@pick-three-ai/types";
import type { LLMModel } from "@pick-three-ai/constants";
import { Download, Link2, Loader2, Check, Copy } from "lucide-react";
import { domToPng } from "modern-screenshot";
import { analytics } from "../lib/analytics";

interface ShareActionsProps {
  rankings: RankingState;
  isComplete: boolean;
  title?: string;
  handle?: string;
  cardRef?: RefObject<HTMLDivElement | null>;
  /** Short code of the ranking being remixed (if any) */
  remixedFromId?: string;
}

// API base URL - in production this would be from env
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8787";

/**
 * Apply rounded corners to an image data URL
 * Returns a new data URL with transparent rounded corners
 */
async function applyRoundedCornersToDataUrl(
  dataUrl: string, 
  borderRadius: number
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      canvas.width = img.width;
      canvas.height = img.height;

      // Draw rounded rectangle path
      ctx.beginPath();
      ctx.moveTo(borderRadius, 0);
      ctx.lineTo(img.width - borderRadius, 0);
      ctx.quadraticCurveTo(img.width, 0, img.width, borderRadius);
      ctx.lineTo(img.width, img.height - borderRadius);
      ctx.quadraticCurveTo(img.width, img.height, img.width - borderRadius, img.height);
      ctx.lineTo(borderRadius, img.height);
      ctx.quadraticCurveTo(0, img.height, 0, img.height - borderRadius);
      ctx.lineTo(0, borderRadius);
      ctx.quadraticCurveTo(0, 0, borderRadius, 0);
      ctx.closePath();

      // Clip to the rounded rectangle and draw the image
      ctx.clip();
      ctx.drawImage(img, 0, 0);

      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = dataUrl;
  });
}

export function ShareActions({
  rankings,
  isComplete,
  title = "",
  handle = "",
  cardRef,
  remixedFromId,
}: ShareActionsProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [justCopied, setJustCopied] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  
  // Use context for persisted createdUrl
  const { createdUrl, setCreatedUrl, createdUrlForState } = useRankingContext();
  
  // Check if current state differs from the state the URL was created for
  const hasContentChanged = useCallback(() => {
    if (!createdUrlForState) return false;
    const { rankings: oldRankings, title: oldTitle, handle: oldHandle } = createdUrlForState;
    return (
      rankings[0].model !== oldRankings[0].model ||
      rankings[0].blurb !== oldRankings[0].blurb ||
      rankings[1].model !== oldRankings[1].model ||
      rankings[1].blurb !== oldRankings[1].blurb ||
      rankings[2].model !== oldRankings[2].model ||
      rankings[2].blurb !== oldRankings[2].blurb ||
      title !== oldTitle ||
      handle !== oldHandle
    );
  }, [rankings, title, handle, createdUrlForState]);
  
  // Reset createdUrl when card content actually changes from the stored state
  useEffect(() => {
    if (createdUrl && createdUrlForState && hasContentChanged()) {
      setCreatedUrl(null);
      setToast("Card changed — Copy another Link to share.");
      setTimeout(() => setToast(null), 2500);
    }
  }, [createdUrl, createdUrlForState, hasContentChanged, setCreatedUrl]);

  const showToast = (message: string) => {
    setToast(message);
    setTimeout(() => setToast(null), 2500);
  };

  const { isGeneric } = useAppMode();

  const handleCopyLink = async () => {
    if (!isComplete || isCopying) return;
    
    analytics.copyLinkClicked();
    setIsCopying(true);
    
    try {
      // Build the ranking payload based on mode
      const payload: RankingInput | GenericRankingInput = isGeneric
        ? {
            rank1: rankings[0].model!,
            rank2: rankings[1].model!,
            rank3: rankings[2].model!,
            blurb1: rankings[0].blurb || undefined,
            blurb2: rankings[1].blurb || undefined,
            blurb3: rankings[2].blurb || undefined,
            title: title || "Best Overall",
            handle: handle || undefined,
            mode: "generic" as const,
            remixedFromId: remixedFromId || undefined,
          }
        : {
            rank1: rankings[0].model as LLMModel,
            rank2: rankings[1].model as LLMModel,
            rank3: rankings[2].model as LLMModel,
            blurb1: rankings[0].blurb || undefined,
            blurb2: rankings[1].blurb || undefined,
            blurb3: rankings[2].blurb || undefined,
            title: title || "Best Overall",
            handle: handle || undefined,
            remixedFromId: remixedFromId || undefined,
          };
      
      // POST to API
      const response = await fetch(`${API_URL}/api/rankings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: "Unknown error" }));
        throw new Error(error.error || "Failed to save ranking");
      }
      
      const data: RankingCreateResponse = await response.json();
      
      // Store the created URL for display FIRST (before clipboard which might fail)
      // Also store the ranking state it was created for to detect changes
      setCreatedUrl(data.url, rankings, title, handle);
      
      // Try to copy URL to clipboard
      try {
        await navigator.clipboard.writeText(data.url);
        analytics.copyLinkCreated(data.shortCode, true);
        setJustCopied(true);
        showToast("Link copied for sharing!");
        setTimeout(() => setJustCopied(false), 2000);
      } catch (clipboardError) {
        // Clipboard failed but URL was created - show a different toast
        analytics.copyLinkCreated(data.shortCode, false);
        console.warn("Clipboard failed:", clipboardError);
        showToast("Link created! Click to copy.");
      }
      
    } catch (error) {
      console.error("Copy link error:", error);
      showToast(error instanceof Error ? error.message : "Failed to create link");
    } finally {
      setIsCopying(false);
    }
  };

  const handleCopyExistingLink = async () => {
    if (!createdUrl || isCopying) return;
    
    try {
      await navigator.clipboard.writeText(createdUrl);
      analytics.copyLinkRecopied(true);
      setJustCopied(true);
      showToast("Link copied for sharing!");
      setTimeout(() => setJustCopied(false), 2000);
    } catch (error) {
      analytics.copyLinkRecopied(false);
      console.error("Copy error:", error);
      showToast("Failed to copy link");
    }
  };

  // Client-side download - uses html2canvas on mobile (more stable), modern-screenshot on desktop (nicer)
  const handleDownload = async () => {
    if (!isComplete || isDownloading || !cardRef?.current) return;
    
    analytics.downloadClicked(isComplete);
    setIsDownloading(true);
    
    // Detect mobile
    const isMobile = window.innerWidth < 640 || 'ontouchstart' in window;
    
    try {
      const cardElement = cardRef.current;
      
      // Store original styles for restoration
      const stylesToRestore: Array<{ element: HTMLElement; property: string; value: string }> = [];
      
      const saveAndSetStyle = (el: HTMLElement, property: string, newValue: string) => {
        stylesToRestore.push({ element: el, property, value: el.style.getPropertyValue(property) });
        el.style.setProperty(property, newValue);
      };
      
      // Hide interactive elements before capture
      const elementsToHide = cardElement.querySelectorAll('[data-hide-on-export="true"]');
      elementsToHide.forEach((el) => {
        saveAndSetStyle(el as HTMLElement, 'display', 'none');
      });
      
      // Show export-only elements
      const elementsToShow = cardElement.querySelectorAll('[data-show-on-export="true"]');
      elementsToShow.forEach((el) => {
        saveAndSetStyle(el as HTMLElement, 'display', 'block');
      });

      // Hide the BackgroundCardGallery during export
      const backgroundGallery = document.querySelector('[data-background-gallery]');
      if (backgroundGallery) {
        saveAndSetStyle(backgroundGallery as HTMLElement, 'visibility', 'hidden');
      }
      
      // Replace backdrop-filter with solid backgrounds for export compatibility
      const mainCard = cardElement.querySelector('.glass-strong');
      if (mainCard) {
        const htmlEl = mainCard as HTMLElement;
        saveAndSetStyle(htmlEl, 'backdrop-filter', 'none');
        saveAndSetStyle(htmlEl, '-webkit-backdrop-filter', 'none');
        saveAndSetStyle(htmlEl, 'background', 'rgba(18, 18, 18, 0.98)');
        // Force desktop padding and border-radius for consistent exports
        saveAndSetStyle(htmlEl, 'padding', '1rem');
        saveAndSetStyle(htmlEl, 'border-radius', '1.5rem');
      }
      
      // Force desktop spacing on ranking slots container for consistent exports
      const haloGold = cardElement.querySelector('.halo-gold');
      if (haloGold) {
        saveAndSetStyle(haloGold as HTMLElement, 'margin-bottom', '1rem');
      }
      
      // Force desktop spacing on 2nd/3rd place container
      const secondThirdContainer = cardElement.querySelector('.halo-gold + div');
      if (secondThirdContainer) {
        const containerEl = secondThirdContainer as HTMLElement;
        // space-y-2 = gap 0.5rem, mx-3 = margin 0.75rem
        saveAndSetStyle(containerEl, 'margin-left', '0.75rem');
        saveAndSetStyle(containerEl, 'margin-right', '0.75rem');
        // Force children spacing (space-y-2)
        const children = containerEl.children;
        for (let i = 1; i < children.length; i++) {
          saveAndSetStyle(children[i] as HTMLElement, 'margin-top', '0.5rem');
        }
      }
      
      // Force desktop padding on ranking slot buttons for consistent exports
      const slotButtons = cardElement.querySelectorAll('.glass > button');
      slotButtons.forEach((btn, index) => {
        const button = btn as HTMLElement;
        const isHero = index === 0;
        // Desktop values: hero = px-4 py-4, others = px-3 py-3
        saveAndSetStyle(button, 'padding-left', isHero ? '1rem' : '0.75rem');
        saveAndSetStyle(button, 'padding-right', isHero ? '1rem' : '0.75rem');
        saveAndSetStyle(button, 'padding-top', isHero ? '1rem' : '0.75rem');
        saveAndSetStyle(button, 'padding-bottom', isHero ? '1rem' : '0.75rem');
        saveAndSetStyle(button, 'gap', '0.75rem');
      });
      
      // Force desktop size on rank badges
      const rankBadges = cardElement.querySelectorAll('.rank-badge-1, .rank-badge-2, .rank-badge-3');
      rankBadges.forEach((badge) => {
        const badgeEl = badge as HTMLElement;
        const isHero = badgeEl.classList.contains('rank-badge-1');
        // Desktop values: hero = w-14 h-14 (3.5rem), others = w-11 h-11 (2.75rem)
        saveAndSetStyle(badgeEl, 'width', isHero ? '3.5rem' : '2.75rem');
        saveAndSetStyle(badgeEl, 'height', isHero ? '3.5rem' : '2.75rem');
      });
      
      // For inner glass elements (slots), disable backdrop-filter
      const innerGlassElements = cardElement.querySelectorAll('.glass:not(.glass-strong)');
      innerGlassElements.forEach((el) => {
        const htmlEl = el as HTMLElement;
        saveAndSetStyle(htmlEl, 'backdrop-filter', 'none');
        saveAndSetStyle(htmlEl, '-webkit-backdrop-filter', 'none');
      });

      // On mobile: disable ALL box-shadows to prevent the shifted glow artifact
      // modern-screenshot has issues rendering box-shadows on mobile Safari
      if (isMobile) {
        const allElements = cardElement.querySelectorAll('*');
        allElements.forEach((el) => {
          const htmlEl = el as HTMLElement;
          const computedStyle = window.getComputedStyle(htmlEl);
          if (computedStyle.boxShadow && computedStyle.boxShadow !== 'none') {
            saveAndSetStyle(htmlEl, 'box-shadow', 'none');
          }
        });
      }

      // If title input is empty (showing placeholder), temporarily set value
      const titleInput = cardElement.querySelector('input[placeholder="Best Overall..."]') as HTMLInputElement | null;
      const originalTitleValue = titleInput?.value ?? '';
      if (titleInput && !titleInput.value) {
        titleInput.value = 'Best Overall';
      }

      // If handle is empty, hide the entire "By @xyz" container
      const handleContainer = cardElement.querySelector('[data-handle-container="true"]') as HTMLElement | null;
      const originalHandleVisibility = handleContainer?.style.visibility ?? '';
      const handleInput = cardElement.querySelector('input[placeholder="fam"]') as HTMLInputElement | null;
      if (handleContainer && handleInput && !handleInput.value) {
        handleContainer.style.visibility = 'hidden';
      }

      // Get border radius from the card element
      const actualCard = cardElement.querySelector('.glass-strong');
      let borderRadiusPx = 16; // fallback
      if (actualCard) {
        const computedRadius = window.getComputedStyle(actualCard).borderRadius;
        const parsedRadius = parseFloat(computedRadius);
        if (!isNaN(parsedRadius) && parsedRadius > 0) {
          borderRadiusPx = parsedRadius;
        }
      }

      const scale = 2;
      
      // Use modern-screenshot (works on both mobile and desktop)
      // On mobile, box-shadows are disabled above to prevent artifacts
      const rawDataUrl = await domToPng(cardElement, {
        scale,
        backgroundColor: "#050505",
        filter: (node) => {
          if (node instanceof HTMLElement) {
            const tagName = node.tagName?.toLowerCase();
            if (tagName === 'iframe' || node.id?.includes('extension')) {
              return false;
            }
          }
          return true;
        },
      });
      
      // Apply rounded corners
      const finalDataUrl = await applyRoundedCornersToDataUrl(rawDataUrl, borderRadiusPx * scale);

      // Restore all modified styles
      stylesToRestore.forEach(({ element, property, value }) => {
        if (value) {
          element.style.setProperty(property, value);
        } else {
          element.style.removeProperty(property);
        }
      });

      // Restore original title value
      if (titleInput) {
        titleInput.value = originalTitleValue;
      }

      // Restore handle container visibility
      if (handleContainer) {
        handleContainer.style.visibility = originalHandleVisibility;
      }

      // Convert data URL to blob and download
      const response = await fetch(finalDataUrl);
      const blob = await response.blob();
      
      // Get title for filename
      const titleForFilename = (titleInput?.value || 'Best Overall')
        .replace(/[^a-zA-Z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Pick-Three-AI-Dec-23-${titleForFilename}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      showToast("Card downloaded!");
    } catch (error) {
      console.error("Download error:", error);
      showToast(error instanceof Error ? error.message : "Failed to download");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="w-full">
      {/* Actions row - Copy Link centered, Download icon on the right */}
      <div className="flex items-center justify-center relative">
        {/* Copy Link - centered */}
        <button
          type="button"
          onClick={createdUrl ? handleCopyExistingLink : handleCopyLink}
          disabled={!isComplete || isCopying}
          className={`
            flex items-center justify-center gap-2
            px-6 py-3 rounded-xl
            font-semibold text-sm
            transition-all duration-300
            disabled:opacity-40 disabled:cursor-not-allowed
            ${justCopied 
              ? "bg-green-500/20 text-green-400 border border-green-500/40" 
              : isComplete 
                ? "bg-accent text-accent-foreground hover:bg-accent-hover copy-link-glow" 
                : "glass text-muted-foreground"
            }
            ${createdUrl ? "max-w-[260px] sm:max-w-[320px]" : ""}
          `}
          title={createdUrl || "Copy link to share"}
        >
          {isCopying ? (
            <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
          ) : justCopied ? (
            <Check className="w-4 h-4 flex-shrink-0" />
          ) : createdUrl ? (
            <Copy className="w-4 h-4 flex-shrink-0" />
          ) : (
            <Link2 className="w-4 h-4 flex-shrink-0" />
          )}
          <span className={createdUrl ? "overflow-x-auto whitespace-nowrap scrollbar-none" : ""}>
            {isCopying ? "Creating..." : justCopied ? "Copied!" : createdUrl ? createdUrl.replace(/^https?:\/\//, '') : "Copy Link"}
          </span>
        </button>

        {/* Download PNG - icon button positioned to the right */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 group">
          <button
            type="button"
            onClick={handleDownload}
            disabled={!isComplete || isDownloading || !cardRef}
            className="
              flex items-center justify-center
              w-10 h-10 rounded-xl
              transition-all duration-200
              disabled:opacity-40 disabled:cursor-not-allowed
              bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white/70 hover:text-white
              border border-white/10 hover:border-white/20
            "
            aria-label="Download Card"
          >
            {isDownloading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
          </button>
          {/* Tooltip */}
          <div className="
            absolute bottom-full left-1/2 -translate-x-1/2 mb-2
            px-2.5 py-1.5 rounded-lg
            bg-[#1a1a1a] text-white text-xs font-medium
            opacity-0 group-hover:opacity-100
            pointer-events-none
            transition-opacity duration-200
            whitespace-nowrap
            border border-white/10
          ">
            Download PNG
            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-[#1a1a1a]" />
          </div>
        </div>
      </div>

      {/* Helper Text */}
      {!isComplete && (
        <p className="text-center text-xs sm:text-sm text-muted-foreground mt-3">
          Select all 3 models to share your ranking
        </p>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div className="glass-strong px-4 py-2.5 rounded-xl text-sm font-medium shadow-lg whitespace-nowrap">
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}
